import { AltArrowExpand } from "@airhelp/icons";
import { Link, Menu, MenuButton, MenuItem, MenuList, VStack, useDisclosure } from "@chakra-ui/react";
import { getLinkSource } from "modules/storyblok/globalComponents/globalComponents.utils";
import { usePathname } from "next/navigation";
import { useMemo } from "react";
import { buildTestId, handleTrackingEvents, isActiveLinkGroup, setLinkStyles } from "../../../Header.utils";
import { HeaderLink } from "../../../HeaderLink";
import type { HeaderLink as HeaderLinkType, HeaderLinksGroup } from "../../../types";

const renderLinksGroupItem = ({
  _uid,
  headerLink,
  headerLinkName,
  headerLinkHideLinkForLanguages,
  headerLinkTrackingEvent,
}: HeaderLinkType) => (
  <MenuItem key={_uid} _focus={{}} p={0} m={0} color="greyscale.600">
    <HeaderLink
      headerLinkName={headerLinkName}
      headerLink={headerLink}
      headerLinkHideLinkForLanguages={headerLinkHideLinkForLanguages}
      headerLinkTrackingEvent={headerLinkTrackingEvent}
    />
  </MenuItem>
);

const menuListExtraStyles = {
  content: "''",
  display: "block",
  height: "25px",
  position: "absolute",
  bottom: "100%",
  right: 0,
  left: 0,
};

export const HeaderLinkGroup = ({
  headerLinksGroupItems,
  headerLinksGroupMainGroupLinkName,
  headerLinksGroupMainGroupLinkSource,
  headerLinksGroupMainGroupLinkNameTrackingEvent,
}: Pick<
  HeaderLinksGroup,
  | "headerLinksGroupItems"
  | "headerLinksGroupMainGroupLinkName"
  | "headerLinksGroupMainGroupLinkSource"
  | "headerLinksGroupMainGroupLinkNameTrackingEvent"
>) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const pathName = usePathname();
  const isActive = useMemo(() => isActiveLinkGroup(headerLinksGroupItems, pathName), [headerLinksGroupItems, pathName]);

  return (
    <Menu isOpen={isOpen} isLazy>
      <MenuButton
        data-testid={`${buildTestId(headerLinksGroupMainGroupLinkName)}-button`}
        sx={setLinkStyles(isActive, "groupLink")}
        data-text={headerLinksGroupMainGroupLinkName}
        onClick={() => handleTrackingEvents(headerLinksGroupMainGroupLinkNameTrackingEvent)}
        as={Link}
        onMouseEnter={onOpen}
        onMouseLeave={onClose}
        href={getLinkSource(headerLinksGroupMainGroupLinkSource)}
      >
        {headerLinksGroupMainGroupLinkName}
        <AltArrowExpand
          ml={2}
          boxSize={6}
          transform={isOpen ? "rotate(180deg)" : "initial"}
          transition="transform 0.2s ease-in-out"
        />
      </MenuButton>
      <MenuList
        pos="relative"
        top={3}
        py={2}
        borderRadius="md"
        boxShadow="md"
        onMouseEnter={onOpen}
        onMouseLeave={onClose}
        _after={menuListExtraStyles}
        data-testid={`${buildTestId(headerLinksGroupMainGroupLinkName)}-list`}
      >
        <VStack px={6} py={3} spacing={6}>
          {headerLinksGroupItems.map(renderLinksGroupItem)}
        </VStack>
      </MenuList>
    </Menu>
  );
};
